import React, { useState } from 'react';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import FormField from '@cloudscape-design/components/form-field';
import Textarea from '@cloudscape-design/components/textarea';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Alert from '@cloudscape-design/components/alert';
import Checkbox from '@cloudscape-design/components/checkbox';
import { updateJobStatus, GroupResult } from '../services/api';
import { validateAction } from './ApprovalWorkflow';

export interface GroupedApprovalWorkflowProps {
  jobId: string;
  groupResults: GroupResult[];
  onAction: () => void;
  duplicatesByGroup: Map<string, [string, string[]][]>;
}

const GroupedApprovalWorkflow: React.FC<GroupedApprovalWorkflowProps> = ({
  jobId,
  groupResults,
  onAction,
  duplicatesByGroup,
}) => {
  const [confirmAction, setConfirmAction] = useState<{
    action: 'complete' | 'reject';
    accountType: string;
  } | null>(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveAsTemplateMap, setSaveAsTemplateMap] = useState<Record<string, boolean>>({});
  const [bulkSaveAsTemplate, setBulkSaveAsTemplate] = useState(true);
  const [templateNotesMap, setTemplateNotesMap] = useState<Record<string, string>>({});
  const [bulkTemplateNotes, setBulkTemplateNotes] = useState('');
  const [completedGroups, setCompletedGroups] = useState<Set<string>>(new Set());
  const [rejectedGroups, setRejectedGroups] = useState<Set<string>>(new Set());

  const hasAnyDuplicates = groupResults.some((g) => {
    const dupes = duplicatesByGroup.get(g.accountType);
    return dupes && dupes.length > 0;
  });

  const groupHasDuplicates = (accountType: string): boolean => {
    const dupes = duplicatesByGroup.get(accountType);
    return dupes !== undefined && dupes.length > 0;
  };

  const handleGroupAction = async (action: 'complete' | 'reject', accountType: string) => {
    if (action === 'reject') {
      const validationError = validateAction(action, reason);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    try {
      const saveAsTemplate = action === 'complete' ? (saveAsTemplateMap[accountType] ?? true) : undefined;
      const notes = action === 'complete' && saveAsTemplate ? (templateNotesMap[accountType] || undefined) : undefined;
      await updateJobStatus(jobId, action, action === 'reject' ? reason : undefined, accountType, saveAsTemplate, notes);
      if (action === 'complete') {
        setCompletedGroups((prev) => new Set(prev).add(accountType));
      } else {
        setRejectedGroups((prev) => new Set(prev).add(accountType));
      }
      setConfirmAction(null);
      setReason('');
      setSaveAsTemplateMap((prev) => { const next = { ...prev }; delete next[accountType]; return next; });
      setTemplateNotesMap((prev) => { const next = { ...prev }; delete next[accountType]; return next; });
      onAction();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkComplete = async () => {
    setSubmitting(true);
    setError(null);
    try {
      for (const group of groupResults) {
        await updateJobStatus(jobId, 'complete', undefined, group.accountType, bulkSaveAsTemplate, bulkSaveAsTemplate ? (bulkTemplateNotes || undefined) : undefined);
      }
      setCompletedGroups(new Set(groupResults.map((g) => g.accountType)));
      setBulkConfirm(false);
      setBulkSaveAsTemplate(true);
      setBulkTemplateNotes('');
      onAction();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (bulkConfirm) {
      await handleBulkComplete();
    } else if (confirmAction) {
      await handleGroupAction(confirmAction.action, confirmAction.accountType);
    }
  };

  const dismissModal = () => {
    if (confirmAction) {
      setSaveAsTemplateMap((prev) => { const next = { ...prev }; delete next[confirmAction.accountType]; return next; });
      setTemplateNotesMap((prev) => { const next = { ...prev }; delete next[confirmAction.accountType]; return next; });
    }
    setConfirmAction(null);
    setBulkConfirm(false);
    setBulkSaveAsTemplate(true);
    setBulkTemplateNotes('');
    setReason('');
    setError(null);
  };

  const modalVisible = confirmAction !== null || bulkConfirm;
  const modalHeader = bulkConfirm
    ? 'Confirm Complete All'
    : confirmAction?.action === 'complete'
      ? `Complete ${confirmAction.accountType}`
      : confirmAction
        ? `Reject ${confirmAction.accountType}`
        : '';

  return (
    <SpaceBetween size="l">
      <Alert type="info">
        Approving types releases the individual records to be sent to SwiftID for processing.
      </Alert>

      {error && !modalVisible && <StatusIndicator type="error">{error}</StatusIndicator>}

      <Button
        variant="primary"
        onClick={() => setBulkConfirm(true)}
        disabled={hasAnyDuplicates || completedGroups.size + rejectedGroups.size === groupResults.length}
        data-testid="bulk-approve-btn"
      >
        Complete All
      </Button>

      {groupResults.map((group) => {
        const hasDupes = groupHasDuplicates(group.accountType);
        const isCompleted = completedGroups.has(group.accountType);
        const isRejected = rejectedGroups.has(group.accountType);
        const isActioned = isCompleted || isRejected;
        return (
          <Container
            key={group.accountType}
            header={<Header variant="h3">{group.accountType}</Header>}
            data-testid={`group-section-${group.accountType}`}
          >
            <SpaceBetween size="s">
              {hasDupes && !isActioned && (
                <Alert type="warning" data-testid={`duplicate-warning-${group.accountType}`}>
                  This group has duplicate target mappings. Resolve duplicates before completing.
                </Alert>
              )}
              {isCompleted && (
                <StatusIndicator type="success">Completed</StatusIndicator>
              )}
              {isRejected && (
                <StatusIndicator type="error">Rejected</StatusIndicator>
              )}
              <SpaceBetween direction="horizontal" size="s">
                <Button
                  variant="primary"
                  onClick={() => setConfirmAction({ action: 'complete', accountType: group.accountType })}
                  disabled={hasDupes || isActioned}
                  data-testid={`approve-btn-${group.accountType}`}
                >
                  Complete
                </Button>
                <Button
                  onClick={() => setConfirmAction({ action: 'reject', accountType: group.accountType })}
                  disabled={isActioned}
                  data-testid={`reject-btn-${group.accountType}`}
                >
                  Reject
                </Button>
              </SpaceBetween>
            </SpaceBetween>
          </Container>
        );
      })}

      <Modal
        visible={modalVisible}
        onDismiss={dismissModal}
        header={modalHeader}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={dismissModal}>Cancel</Button>
              <Button variant="primary" loading={submitting} onClick={handleConfirm}>
                {bulkConfirm || confirmAction?.action === 'complete' ? 'Complete' : 'Reject'}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {bulkConfirm && (
            <SpaceBetween size="m">
              <Box>Are you sure you want to complete all groups?</Box>
              <Checkbox
                checked={bulkSaveAsTemplate}
                onChange={({ detail }) => setBulkSaveAsTemplate(detail.checked)}
              >
                Also save as template
              </Checkbox>
              {bulkSaveAsTemplate && (
                <FormField label="Template notes (optional)">
                  <Textarea
                    value={bulkTemplateNotes}
                    onChange={({ detail }: { detail: { value: string } }) => setBulkTemplateNotes(detail.value)}
                    placeholder="e.g. Broker X standard format, verified April 2026"
                  />
                </FormField>
              )}
            </SpaceBetween>
          )}
          {!bulkConfirm && confirmAction?.action === 'complete' && (
            <SpaceBetween size="m">
              <Box>
                Are you sure you want to complete the {confirmAction.accountType} group?
              </Box>
              <Checkbox
                checked={saveAsTemplateMap[confirmAction.accountType] ?? true}
                onChange={({ detail }) =>
                  setSaveAsTemplateMap((prev) => ({ ...prev, [confirmAction.accountType]: detail.checked }))
                }
              >
                Also save as template
              </Checkbox>
              {(saveAsTemplateMap[confirmAction.accountType] ?? true) && (
                <FormField label="Template notes (optional)">
                  <Textarea
                    value={templateNotesMap[confirmAction.accountType] ?? ''}
                    onChange={({ detail }: { detail: { value: string } }) =>
                      setTemplateNotesMap((prev) => ({ ...prev, [confirmAction.accountType]: detail.value }))
                    }
                    placeholder="e.g. Broker X standard format, verified April 2026"
                  />
                </FormField>
              )}
            </SpaceBetween>
          )}
          {confirmAction?.action === 'reject' && (
            <FormField label="Rejection reason" errorText={error}>
              <Textarea
                value={reason}
                onChange={({ detail }: { detail: { value: string } }) => setReason(detail.value)}
                placeholder="Provide a reason for rejection"
              />
            </FormField>
          )}
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
};

export default GroupedApprovalWorkflow;
